import { Injectable } from '@angular/core';

/**
 * Keys stored in localStorage — shared across all tabs.
 * Used only for authentication tokens and app versioning.
 */
export enum LocalStorageKey {
  Token = 'token',
  RefreshToken = 'refreshToken',
  AppVersion = 'app_version',
}

/**
 * Keys stored in sessionStorage — isolated per tab.
 * Used for request data, user preferences, and form state.
 */
export enum SessionStorageKey {
  RequestNumber = 'requestNumber',
  RequestId = 'requestId',
  SelectedLaw = 'selectedLaw',
  SelectedLegalSituation = 'selectedLegalSituation',
  SelectedEstablishmentType = 'selectedEstablishmentType',
  SelectedInvestmentNature = 'selectedInvestmentNature',
  SelectedFreeZoneType = 'selectedFreeZoneType',
  Status = 'status',
  IsVip = 'isVip',
  UserType = 'userType',
  SendDate = 'sendDate',
}

/**
 * StorageService provides a clean API for browser storage.
 *
 * - sessionStorage (default): tab-isolated data (requests, form state).
 * - localStorage: only for auth tokens and app version that must persist across tabs.
 *
 * Use this service instead of calling localStorage/sessionStorage directly.
 */
@Injectable({
  providedIn: 'root',
})
export class StorageService {

  // ─── Session Storage (tab-isolated) ───

  setSession(key: SessionStorageKey, value: string): void {
    sessionStorage.setItem(key, value);
  }

  getSession(key: SessionStorageKey): string | null {
    return sessionStorage.getItem(key);
  }

  removeSession(key: SessionStorageKey): void {
    sessionStorage.removeItem(key);
  }

  setSessionJson<T>(key: SessionStorageKey, value: T): void {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  getSessionJson<T>(key: SessionStorageKey): T | null {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      console.error(`Failed to parse session key "${key}"`, raw);
      return null;
    }
  }

  hasSession(key: SessionStorageKey): boolean {
    return sessionStorage.getItem(key) !== null;
  }

  clearSession(): void {
    sessionStorage.clear();
  }

  // ─── Local Storage (shared across tabs) ───

  setLocal(key: LocalStorageKey, value: string): void {
    localStorage.setItem(key, value);
  }

  getLocal(key: LocalStorageKey): string | null {
    return localStorage.getItem(key);
  }

  removeLocal(key: LocalStorageKey): void {
    localStorage.removeItem(key);
  }

  hasLocal(key: LocalStorageKey): boolean {
    return localStorage.getItem(key) !== null;
  }

  /**
   * Clear all localStorage keys except the given exclusions.
   */
  clearLocal(exclude: LocalStorageKey[] = []): void {
    Object.keys(localStorage).forEach((key) => {
      if (!exclude.includes(key as LocalStorageKey)) {
        localStorage.removeItem(key);
      }
    });
  }
}
